import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

interface Props {
  /** Version string that was inlined at build/render time */
  initial: string;
  /** How often (ms) to check for updates */
  interval?: number;
}

const VERSION_KEY = 'frontpage-version';
const LAST_REFRESH_KEY = 'frontpage-last-refresh';
// const REFRESH_COOLDOWN = 60 * 60 * 1000; //
//  1 hour in milliseconds
const REFRESH_COOLDOWN = 1000;

export default function VersionWatcher({ initial, interval = 30_000 }: Props) {
  const router = useRouter();
  const current = useRef(initial);

  useEffect(() => {
    const checkVersion = async () => {
      console.log('🔍 Checking frontpage version...');
      
      try {
        const response = await fetch('/api/frontpage-version', {
          cache: 'no-store',
        });
        const data = await response.json();
        
        if (!data.version) {
          console.log('❌ No version returned from API');
          return;
        }

        const storedVersion = localStorage.getItem(VERSION_KEY);
        const lastRefresh = localStorage.getItem(LAST_REFRESH_KEY);
        const lastRefreshTime = lastRefresh ? new Date(lastRefresh).getTime() : 0;
        const timeSinceLastRefresh = Date.now() - lastRefreshTime;
        
        console.log('📦 Stored version:', storedVersion);
        console.log('🕒 Last refresh:', lastRefresh ? new Date(lastRefresh).toLocaleString() : 'Never');
        console.log('⏱️ Time since last refresh:', Math.round(timeSinceLastRefresh / 1000 / 60), 'minutes');
        console.log('🔄 New version:', data.version);
        
        // If no stored version, store current and continue
        if (!storedVersion) {
          console.log('📝 No stored version found, storing current version');
          localStorage.setItem(VERSION_KEY, data.version);
          return;
        }

        // If versions differ, check cooldown before refreshing
        if (data.version !== storedVersion) {
          console.log('🔄 Version mismatch detected!');
          
          if (timeSinceLastRefresh < REFRESH_COOLDOWN) {
            console.log('⏳ Skipping refresh - cooldown period not elapsed');
            console.log('📝 Updating stored version without refresh');
            // localStorage.setItem(VERSION_KEY, data.version);
          } else {
            console.log('📝 Updating stored version and refreshing...');
            localStorage.setItem(VERSION_KEY, data.version);
            localStorage.setItem(LAST_REFRESH_KEY, new Date().toISOString());
            router.reload();
          }
        } else {
          console.log('✅ Versions match, continuing...');
        }
      } catch (error) {
        console.error('❌ Error checking version:', error);
      }
    };

    // Check immediately on load
    console.log('🚀 Initial version check...');
    checkVersion();
    
    // Then set up the interval
    console.log(`⏰ Setting up version check every ${interval}ms`);
    const timer = setInterval(checkVersion, interval);
    return () => clearInterval(timer);
  }, [interval, router]);

  return null;
} 